import { useEffect, useState } from "react";
import { API } from "@/lib/api";
import { formatINR } from "@/lib/api";
import { X, Loader2 } from "lucide-react";

export default function SalesLinesDialog({ open, onClose, filter }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!open || !filter) return;
    setData(null);
    setError("");
    const p = new URLSearchParams();
    if (filter.customer) p.set("customer", filter.customer);
    if (filter.material) p.set("material", filter.material);
    if (filter.plant) p.set("plant", filter.plant);
    if (filter.month) p.set("month", filter.month);
    if (filter.from_date) p.set("from_date", filter.from_date);
    if (filter.to_date) p.set("to_date", filter.to_date);
    fetch(`${API}/reports/sales-performance/lines?${p}`, { credentials: "include" })
      .then((r) => (r.ok ? r.json() : r.json().then((j) => Promise.reject(new Error(j.detail || "Failed")))))
      .then(setData)
      .catch((e) => setError(e.message));
  }, [open, filter]);

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" data-testid="sales-lines-dialog" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-5xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between px-5 py-3.5 border-b border-slate-200">
          <div>
            <h3 className="font-semibold text-slate-900 text-sm" data-testid="sales-lines-title">{filter?.title || "Invoice lines"}</h3>
            {data && (
              <p className="text-xs text-slate-500 mt-0.5">
                {data.totals.invoices} invoices · {data.count.toLocaleString("en-IN")} lines · {formatINR(data.totals.value)}
              </p>
            )}
          </div>
          <button onClick={onClose} data-testid="sales-lines-close" className="text-slate-400 hover:text-slate-700"><X size={18} /></button>
        </div>
        <div className="overflow-auto thin-scroll flex-1">
          {error && <div className="p-6 text-sm text-red-600">{error}</div>}
          {!data && !error && <div className="p-10 flex justify-center"><Loader2 className="animate-spin text-slate-400" /></div>}
          {data && (
            <table className="w-full text-[12.5px]">
              <thead className="sticky top-0 bg-slate-50">
                <tr className="border-b border-slate-200 text-[10.5px] uppercase tracking-wider text-slate-500">
                  <th className="text-left px-3 py-2">Date</th>
                  <th className="text-left px-3 py-2">Invoice</th>
                  <th className="text-left px-3 py-2">Type</th>
                  <th className="text-left px-3 py-2">Customer</th>
                  <th className="text-left px-3 py-2">Material</th>
                  <th className="text-left px-3 py-2">Description</th>
                  <th className="text-left px-3 py-2">Plant</th>
                  <th className="text-right px-3 py-2">Qty</th>
                  <th className="text-right px-3 py-2">Net Value</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.map((r, i) => (
                  <tr key={i} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`sales-line-${i}`}>
                    <td className="px-3 py-1.5 whitespace-nowrap">{r.date}</td>
                    <td className="px-3 py-1.5 font-mono text-[11.5px]">{r.invoice}</td>
                    <td className={`px-3 py-1.5 text-[11px] ${r.value < 0 ? "text-red-600 font-medium" : "text-slate-500"}`}>
                      {r.btype || "—"}{r.value < 0 ? " (credit/return)" : ""}
                    </td>
                    <td className="px-3 py-1.5">{r.customer_name || r.customer_code}</td>
                    <td className="px-3 py-1.5 font-mono text-[11.5px]">{r.material}</td>
                    <td className="px-3 py-1.5 text-slate-500 max-w-[220px] truncate">{r.desc}</td>
                    <td className="px-3 py-1.5">{r.plant}</td>
                    <td className="px-3 py-1.5 text-right font-mono">{(r.qty || 0).toLocaleString("en-IN")}</td>
                    <td className={`px-3 py-1.5 text-right font-mono ${r.value < 0 ? "text-red-600" : ""}`}>{formatINR(r.value)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
